import { ChakraProvider } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AnimatePresence } from 'framer-motion';
import * as React from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AccountSettings } from './components/AccountSettings';
import AuthenticatedDesk from './components/Desk';
import { EditorRoot } from './components/Editor/EditorRoot';
import { HelpAndFeedback } from './components/HelpAndFeedback';
import { InvitationManager, WriteToMe } from './components/InvitationManagement';
import { AuthenticatedInvitations } from './components/Invitations';
import InviteWithLetter from './components/InviteWithLetter';
import LetterInvite from './components/LetterInvite';
import { AuthenticatedLetterViewer } from './components/LetterViewer';
import Maintenance from './components/Maintenance';
import { Nav } from './components/Nav';
import { NewLetter } from './components/NewLetter';
import { NotFound } from './components/NotFound';
import { AuthenticatedPreComposer } from './components/PreComposer';
import { useInvitationsHandler } from './components/hooks/useInvitations';
import { useTrackOnlineStatus } from './components/hooks/useOnlineStatus';
import ForgotYourPassword from './components/user/ForgotYourPassword';
import { LogIn } from './components/user/LogIn';
import RegisterOrWaitlist from './components/user/Register';
import ResetYourPassword from './components/user/ResetYourPassword';
import { routes } from './data/routes';
import { useSessionHandler, useSessionRefresh } from './lib/Session';
import { theme } from './theme';

const queryClient = new QueryClient();

const Singletons = () => {
  useTrackOnlineStatus();
  useInvitationsHandler();

  useSessionHandler();
  useSessionRefresh();

  return (
    <>
      <InvitationManager />
    </>
  );
};

export const App = () => (
  <QueryClientProvider client={queryClient}>
    <ReactQueryDevtools initialIsOpen={false} />
    <ChakraProvider theme={theme}>
      <Maintenance>
        <BrowserRouter>
          <Singletons />
          <Nav></Nav>
          <AnimatePresence mode="wait">
            <Routes location={location}>
              <Route path={routes.root} element={<AuthenticatedDesk />} />
              <Route path={routes.letterViewer} element={<AuthenticatedLetterViewer />} />
              <Route path={routes.newLetter} element={<NewLetter />} />
              <Route path={routes.preComposer} element={<AuthenticatedPreComposer />} />
              <Route path={routes.composer} element={<EditorRoot />} />
              <Route path={routes.writeToMe} element={<WriteToMe />} />
              <Route path={routes.letterInvite} element={<LetterInvite />} />
              <Route path={routes.logIn} element={<LogIn />} />
              <Route path={routes.register} element={<RegisterOrWaitlist />} />
              <Route path={routes.forgotPassword} element={<ForgotYourPassword />} />
              <Route path={routes.passwordReset} element={<ResetYourPassword />} />
              <Route path={routes.invitations} element={<AuthenticatedInvitations />} />
              <Route path={routes.inviteWithLetter} element={<InviteWithLetter />} />
              <Route path={routes.helpAndFeedback} element={<HelpAndFeedback />} />
              <Route path={routes.accountSettings} element={<AccountSettings />} />
              <Route element={<NotFound />} />
            </Routes>
          </AnimatePresence>
        </BrowserRouter>
      </Maintenance>
    </ChakraProvider>
  </QueryClientProvider>
);
